import { useCallback, useEffect, useRef, useState } from 'react';

export type EstadoRecurso = 'carregando' | 'erro' | 'pronto';

export interface Recurso<T> {
  dados: T | null;
  estado: EstadoRecurso;
  erro: Error | null;
  /** Recarrega do zero — é o que o botão "Tentar de novo" chama. */
  recarregar: () => void;
  /**
   * Busca de novo sem piscar "carregando"/"erro" — para quando algo externo
   * (WebSocket, foco de aba) avisa que os dados podem ter mudado, mas a pessoa
   * não pediu a atualização explicitamente.
   */
  atualizarEmSilencio: () => void;
  /** Atualiza o cache local sem ir ao servidor (após uma mutação otimista). */
  definir: (proximo: T) => void;
}

/**
 * Carrega um recurso assíncrono expondo exatamente os estados que o playbook
 * exige de toda tela: carregando, erro (com "tentar de novo") e pronto. O
 * quarto estado — vazio — é do componente que renderiza, porque só ele sabe o
 * que "vazio" significa naquele contexto.
 *
 * Deliberadamente não é um cliente de cache: o PAN tem sete telas de leitura e
 * uma de escrita; uma dependência de data-fetching aqui seria peso sem retorno.
 *
 * `intervaloAtualizacaoMs` cobre a lacuna de não termos um canal em tempo real
 * (websocket/SSE): sem isso, duas pessoas olhando o mesmo horário/agendamento
 * nunca veem a mudança uma da outra até recarregar a página manualmente — só o
 * servidor (autoritativo) barra a ação em conflito, a tela fica desatualizada.
 * A atualização de fundo é silenciosa — não pisca "carregando" nem "erro" — só
 * o retorno do "Tentar de novo" explícito usa esses estados.
 */
export function useResource<T>(
  carregar: (signal: AbortSignal) => Promise<T>,
  deps: unknown[] = [],
  opcoes?: { intervaloAtualizacaoMs?: number },
): Recurso<T> {
  const [dados, setDados] = useState<T | null>(null);
  const [erro, setErro] = useState<Error | null>(null);
  const [estado, setEstado] = useState<EstadoRecurso>('carregando');
  const [tentativa, setTentativa] = useState(0);
  const carregarRef = useRef(carregar);
  carregarRef.current = carregar;
  const silenciosoRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    let ativo = true;
    const silencioso = silenciosoRef.current;
    silenciosoRef.current = false;

    if (!silencioso) {
      setEstado('carregando');
      setErro(null);
    }

    carregarRef
      .current(controller.signal)
      .then((resultado) => {
        if (!ativo) return;
        setDados(resultado);
        setEstado('pronto');
      })
      .catch((causa: unknown) => {
        if (!ativo || (causa instanceof DOMException && causa.name === 'AbortError')) return;
        if (silencioso) return; // atualização de fundo falhou: mantém o que já estava na tela
        setErro(causa instanceof Error ? causa : new Error(String(causa)));
        setEstado('erro');
      });

    return () => {
      ativo = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tentativa, ...deps]);

  const recarregar = useCallback(() => setTentativa((valor) => valor + 1), []);
  const atualizarEmSilencio = useCallback(() => {
    silenciosoRef.current = true;
    setTentativa((valor) => valor + 1);
  }, []);
  const definir = useCallback((proximo: T) => setDados(proximo), []);

  const intervaloMs = opcoes?.intervaloAtualizacaoMs;
  useEffect(() => {
    if (!intervaloMs) return;

    function aoFicarVisivel() {
      if (document.visibilityState === 'visible') atualizarEmSilencio();
    }

    const id = window.setInterval(atualizarEmSilencio, intervaloMs);
    document.addEventListener('visibilitychange', aoFicarVisivel);
    window.addEventListener('focus', atualizarEmSilencio);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', aoFicarVisivel);
      window.removeEventListener('focus', atualizarEmSilencio);
    };
  }, [intervaloMs, atualizarEmSilencio]);

  return { dados, estado, erro, recarregar, atualizarEmSilencio, definir };
}
